import { useEffect, useCallback, useRef } from "react";
import { useWebSocket } from "@/hooks/useWebSocket";
import {
  WebSocketMessage,
  XPAwardMessage,
  GenerationProgressMessage,
  TranslationProgressMessage,
  GeneralNotificationMessage,
} from "@/contexts/WebSocketTypes";

// Constants for message types - must match backend MESSAGE_TYPES
const MESSAGE_TYPES = {
  XP_AWARD: "xp_award",
  GENERATION_PROGRESS: "generation_progress",
  TRANSLATION_PROGRESS: "translation_progress",
  GENERAL_NOTIFICATION: "general_notification",
  PIPELINE_PROGRESS: "pipeline_progress",
} as const;

// Custom events for decoupled message handling
const WEBSOCKET_EVENTS = {
  XP_AWARD: "websocket:xp-award",
  GENERATION_PROGRESS: "websocket:generation-progress",
  TRANSLATION_PROGRESS: "websocket:translation-progress",
  GENERAL_NOTIFICATION: "websocket:general-notification",
  PIPELINE_PROGRESS: "websocket:pipeline-progress",
} as const;

/**
 * Centralized WebSocket message router that dispatches messages as custom events
 * This prevents duplicate processing and provides a clean separation of concerns
 */
export const useWebSocketMessageRouter = () => {
  const { lastMessage } = useWebSocket();
  const processedMessagesRef = useRef<Set<string>>(new Set());

  const dispatchWebSocketEvent = useCallback(
    (eventType: string, data: WebSocketMessage) => {
      window.dispatchEvent(
        new CustomEvent(eventType, {
          detail: data,
          bubbles: false,
          cancelable: false,
        }),
      );
    },
    [],
  );
  const generateMessageId = useCallback((data: WebSocketMessage): string => {
    // Create a unique ID based on message content to prevent duplicates
    switch (data.type) {
      case MESSAGE_TYPES.XP_AWARD: {
        const xpData = data as XPAwardMessage;
        return `xp_${xpData.user_id}_${xpData.timestamp}_${xpData.xp_earned}`;
      }
      case MESSAGE_TYPES.GENERATION_PROGRESS: {
        const genData = data as GenerationProgressMessage;
        return `gen_${genData.item_id}_${genData.status}_${genData.version || "none"}_${Date.now()}`;
      }
      case MESSAGE_TYPES.TRANSLATION_PROGRESS: {
        const transData = data as TranslationProgressMessage;
        return `trans_${transData.item_id}_${transData.version}_${transData.lang}_${transData.status}`;
      }
      case MESSAGE_TYPES.GENERAL_NOTIFICATION: {
        const notificationData = data as GeneralNotificationMessage;
        return `notification_${notificationData.user_id}_${notificationData.timestamp}`;
      }
      default:
        return `unknown_${Date.now()}_${Math.random()}`;
    }
  }, []);

  const routeMessage = useCallback(
    (data: WebSocketMessage) => {
      const messageId = generateMessageId(data);

      // Check if we've already processed this message
      if (processedMessagesRef.current.has(messageId)) {
        console.debug(
          `[WebSocketRouter] Duplicate message ignored: ${messageId}`,
        );
        return;
      }

      // Mark message as processed
      processedMessagesRef.current.add(messageId);

      // Clean up old message IDs to prevent memory leaks (keep last 100)
      if (processedMessagesRef.current.size > 100) {
        const entries = Array.from(processedMessagesRef.current);
        const toKeep = entries.slice(-50); // Keep only the last 50
        processedMessagesRef.current = new Set(toKeep);
      }

      switch (data.type) {
        case MESSAGE_TYPES.XP_AWARD:
          dispatchWebSocketEvent(
            WEBSOCKET_EVENTS.XP_AWARD,
            data as XPAwardMessage,
          );
          break;

        case MESSAGE_TYPES.GENERATION_PROGRESS:
          dispatchWebSocketEvent(
            WEBSOCKET_EVENTS.GENERATION_PROGRESS,
            data as GenerationProgressMessage,
          );
          break;

        case MESSAGE_TYPES.TRANSLATION_PROGRESS:
          dispatchWebSocketEvent(
            WEBSOCKET_EVENTS.TRANSLATION_PROGRESS,
            data as TranslationProgressMessage,
          );
          break;

        case MESSAGE_TYPES.GENERAL_NOTIFICATION:
          dispatchWebSocketEvent(
            WEBSOCKET_EVENTS.GENERAL_NOTIFICATION,
            data as GeneralNotificationMessage,
          );
          break;

        case MESSAGE_TYPES.PIPELINE_PROGRESS:
          // Pipeline progress messages are handled directly in Pipeline pages
          // No need to dispatch as custom event - already handled via lastMessage
          break;

        default: {
          // Type assertion for unknown message types
          const unknownMessage = data as { type?: string };
          console.warn(
            `[WebSocketRouter] Unknown message type: ${unknownMessage.type || "undefined"}`,
          );
          // Log additional info for debugging
          console.warn("[WebSocketRouter] Full message data:", data);
          break;
        }
      }
    },
    [dispatchWebSocketEvent, generateMessageId],
  );

  useEffect(() => {
    if (!lastMessage) return;

    try {
      const data: WebSocketMessage = JSON.parse(lastMessage.data);
      routeMessage(data);
    } catch (error) {
      console.error("[WebSocketRouter] Error parsing message:", error, {
        messageData: lastMessage.data,
      });
    }
  }, [lastMessage, routeMessage]);

  return {
    // Export event constants for use by consuming hooks
    WEBSOCKET_EVENTS,
    MESSAGE_TYPES,
  };
};

/**
 * Helper hook for subscribing to specific WebSocket message types
 * Usage: useWebSocketEvent(WEBSOCKET_EVENTS.XP_AWARD, handleXPAward)
 */
export const useWebSocketEvent = <T = unknown>(
  eventType: string,
  handler: (data: T) => void,
  dependencies: React.DependencyList = [],
) => {
  useEffect(() => {
    const handleEvent = (event: CustomEvent<T>) => {
      handler(event.detail);
    };

    window.addEventListener(eventType, handleEvent as EventListener);

    return () => {
      window.removeEventListener(eventType, handleEvent as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventType, handler, ...dependencies]);
};
